import { Frequency } from "@convin/type/Analytics";
import { PaginationType, ShareSnippet } from "@convin/type/Common";
import {
    AiInsightDataByType,
    CIBarGraphData,
    CIFiltersPayload,
    CIInsight,
    CILineGraphData,
    ConversationsWithSnippets,
    CreateAIFilterPayload,
    CustomTrackingPatchResponse,
    CustomTrackingPayload,
    CustomTrackingTab,
    CustomTrackingTabWithPhrase,
    DetailedInsightDataByType,
    ErrorCheckPayload,
    ISubInsight,
    AIFilter,
    InsightType,
    InsightDashboardDetails,
    InsightDashboardDetailsPayload,
    InsightDataByType,
    InsightDetail,
    InsightStatistics,
    InsightSummary,
    InsightTopics,
    InsightTrendData,
    InsightTrendDataPayload,
    InsightsDownloadPayload,
    InsightsPayload,
    InsightsStatsPayload,
    NewSubInsightPayload,
    PhraseStatistics,
    CIInsightResponse,
    CIGPTType,
    CIGPTQuestion,
} from "@convin/type/customerIntelligence";
import { DownloadReport } from "@convin/type/Report";
import {
    isDefined,
    updateDownloadQue,
} from "@convin/utils/helper/common.helper";
import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import { CIdashboardType } from "@convin/modules/customerInteligence/modules/ConvinInsights/context/ContextDefinitions";

const getApiRoute = (dashboardType: CIdashboardType) =>
    dashboardType === "ai_insights"
        ? "/gpt/ai_insight/"
        : "/customer_intelligence/";

export const customerIntelligenceApiSlice = createApi({
    reducerPath: "customerIntelligenceApiSlice",
    baseQuery: axiosBaseQuery(),
    tagTypes: [
        "CI_TAB",
        "CI_GRAPH",
        "Insights",
        "InsightDetails",
        "TrendData",
        "InsightStats",
        "InsightTopics",
        "InternalCI",
        "AiInsights",
    ],
    endpoints: (builder) => ({
        // CUSTOM TRACKING
        getCITabs: builder.query<CustomTrackingTab[], void>({
            query: () => ({
                url: "customer_intelligence/customer_intelligence/tabs/",
                method: "POST",
                body: {},
            }),
            providesTags: [{ type: "CI_TAB", id: "LIST" }],
        }),
        getCITab: builder.query<
            CustomTrackingTabWithPhrase,
            { id: number; payload: CustomTrackingPayload }
        >({
            query: ({ payload, id }) => ({
                url: `customer_intelligence/customer_intelligence/tabs/${id}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (result, error, { id }) => [{ type: "CI_TAB", id }],
        }),
        getCITabGraph: builder.query<
            CILineGraphData | CIBarGraphData,
            {
                id: number;
                payload: CustomTrackingPayload & {
                    is_line_graph: boolean;
                    freq?: Frequency;
                };
            }
        >({
            query: ({ payload, id }) => ({
                url: `customer_intelligence/customer_intelligence/tabs/${id}/graph/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (result, error, { id }) => [{ type: "CI_GRAPH", id }],
        }),
        updateCITabSequence: builder.mutation<
            CustomTrackingTab[],
            CustomTrackingTab[]
        >({
            query: (payload) => ({
                url: "customer_intelligence/customer_intelligence/tabs/seqno/update/",
                method: "PATCH",
                body: {
                    tab_data: payload.map(({ id, seq_no }) => ({ id, seq_no })),
                },
            }),
            async onQueryStarted(payload, { queryFulfilled, dispatch }) {
                const patchResult = dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITabs",
                        undefined,
                        () => payload
                    )
                );
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        customerIntelligenceApiSlice.util.upsertQueryData(
                            "getCITabs",
                            undefined,
                            data
                        )
                    );
                } catch {
                    patchResult.undo();
                }
            },
        }),
        createCITab: builder.mutation<
            CustomTrackingTab,
            Pick<CustomTrackingTab, "title">
        >({
            query: (payload) => ({
                url: "customer_intelligence/customer_intelligence/tabs/create/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITabs",
                        undefined,
                        (draft) => [
                            data,
                            ...draft.map((tab, index) => ({
                                ...tab,
                                seq_no: index + 1,
                            })),
                        ]
                    )
                );
            },
        }),
        updateCITab: builder.mutation<
            CustomTrackingTab,
            Pick<CustomTrackingTab, "id" | "title">
        >({
            query: ({ id, ...payload }) => ({
                url: `customer_intelligence/customer_intelligence/tabs/${id}/`,
                method: "PATCH",
                body: payload,
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITabs",
                        undefined,
                        (draft) => draft.map((e) => (e.id === +id ? data : e))
                    )
                );
            },
        }),
        deleteCITab: builder.mutation<void, number>({
            query: (id) => ({
                url: `customer_intelligence/customer_intelligence/tabs/${id}/`,
                method: "DELETE",
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITabs",
                        undefined,
                        (draft) =>
                            draft.map((e) =>
                                e.id === +id ? { ...e, is_deleted: true } : e
                            )
                    )
                );
            },
        }),
        getCIPhraseSnippets: builder.query<
            PaginationType<ConversationsWithSnippets>,
            {
                url: string;
                next?: string;
                payload: CustomTrackingPayload | CIFiltersPayload;
            }
        >({
            query: ({ url, next, payload }) => ({
                url: next || url,
                method: "POST",
                body: payload,
            }),
        }),
        addCITabPhrase: builder.mutation<
            CustomTrackingTabWithPhrase,
            {
                tab: number;
                phrase: string;
                representation: string;
                payload: CustomTrackingPayload;
            }
        >({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            query: ({ payload: _, ...rest }) => ({
                url: "/customer_intelligence/search_phrase/v2/",
                method: "POST",
                body: rest,
            }),
            invalidatesTags: (_, error, arg) =>
                !isDefined(error) ? [{ type: "CI_GRAPH", id: arg.tab }] : [],
            async onQueryStarted(
                { tab, payload },
                { dispatch, queryFulfilled }
            ) {
                const { data } = await queryFulfilled;
                dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITab",
                        { id: tab, payload },
                        (draft) => Object.assign(draft, data)
                    )
                );
            },
        }),
        updateCITabPhrase: builder.mutation<
            CustomTrackingPatchResponse,
            {
                tab: number;
                id: number;
                representation: string;
                payload: CustomTrackingPayload;
            }
        >({
            query: ({ id, representation }) => ({
                url: `/customer_intelligence/search_phrase/${id}/`,
                method: "PATCH",
                body: {
                    representation,
                },
            }),
            invalidatesTags: (result, error, arg) =>
                !isDefined(error) ? [{ type: "CI_GRAPH", id: arg.tab }] : [],
            async onQueryStarted(
                { id, tab, payload },
                { dispatch, queryFulfilled }
            ) {
                const { data } = await queryFulfilled;
                dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITab",
                        { id: tab, payload },
                        (draft) =>
                            Object.assign(draft, {
                                ...draft,
                                search_phrases: draft?.search_phrases?.map(
                                    (e) =>
                                        e.id === id
                                            ? {
                                                  ...e,
                                                  representation:
                                                      data?.representation,
                                              }
                                            : e
                                ),
                            })
                    )
                );
            },
        }),
        removeCITabPhrase: builder.mutation<
            void,
            {
                tab: number;
                id: number;
                payload: CustomTrackingPayload;
            }
        >({
            query: ({ id }) => ({
                url: `/customer_intelligence/search_phrase/${id}/`,
                method: "DELETE",
            }),
            async onQueryStarted(
                { id, payload, tab },
                { dispatch, queryFulfilled }
            ) {
                const deleteResult = dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCITab",
                        { payload, id: tab },
                        (draft) => {
                            Object.assign(draft, {
                                ...draft,
                                search_phrases: draft?.search_phrases?.map(
                                    (e) =>
                                        e.id !== id
                                            ? e
                                            : { ...e, is_processed: "deleting" }
                                ),
                            });
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    deleteResult.undo();
                }
            },
            invalidatesTags: (_, error, arg) =>
                !isDefined(error) ? [{ type: "CI_GRAPH", id: arg.tab }] : [],
        }),
        sendWordCloud: builder.mutation<
            DownloadReport,
            {
                id: number;
                payload: Omit<CustomTrackingPayload, "exclude">;
            }
        >({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/search_phrase/${id}/word_cloud/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        sendReport: builder.mutation<
            DownloadReport,
            {
                id: number;
                payload: Omit<CustomTrackingPayload, "exclude"> & {
                    is_tab_level?: boolean;
                };
            }
        >({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/search_phrase/${id}/download/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        sendTabReport: builder.mutation<
            DownloadReport,
            {
                id: number;
                payload: CustomTrackingPayload;
            }
        >({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/ci_tab_report/${id}/download/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        getPhraseStatistics: builder.query<
            PhraseStatistics,
            {
                id: number;
                payload: CIFiltersPayload;
            }
        >({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/search_phrase_stats/${id}/`,
                method: "POST",
                body: payload,
            }),
        }),

        getInsightSummary: builder.query<
            InsightSummary,
            InsightsPayload["payload"]
        >({
            query: (payload) => ({
                url: "/customer_intelligence/ci_data/",
                method: "POST",
                body: payload,
            }),
        }),
        getInsightByType: builder.query<InsightDataByType, InsightsPayload>({
            query: ({ type, payload }) => ({
                url: `/customer_intelligence/insight/${type}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (_, __, arg) => [{ type: "Insights", id: arg.type }],
        }),
        getCIInsightByType: builder.query<
            InsightDataByType | AiInsightDataByType,
            InsightsPayload
        >({
            query: ({ type, payload }) => ({
                url: `/customer_intelligence/insight/${type}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (_, __, arg) => [{ type: "Insights", id: arg.type }],
        }),
        getAllInsightsByType: builder.query<
            { insights: InsightDetail[] },
            {
                type: InsightsPayload["type"];
                dashboardType: CIdashboardType;
            }
        >({
            query: ({ type, dashboardType }) => ({
                url: `${getApiRoute(dashboardType)}all_insights/${type}/`,
                method: "GET",
            }),
            providesTags: (_, __, arg) => [{ type: "Insights", id: arg.type }],
        }),
        getInsightDashboardDetails: builder.query<
            InsightDashboardDetails,
            InsightDashboardDetailsPayload & { dashboardType: CIdashboardType }
        >({
            query: ({ dashboardType, ...rest }) => ({
                url: `${getApiRoute(dashboardType)}insight_dashboards/${
                    dashboardType === "ai_insights" ? `${rest.type}/` : ""
                }`,
                method: "POST",
                body: rest.payload,
            }),
            providesTags: (_, __, arg) => [{ type: "Insights", id: arg.type }],
        }),
        getInsightTrendData: builder.query<
            InsightTrendData[],
            InsightTrendDataPayload & { dashboardType: CIdashboardType }
        >({
            query: ({ type, payload, dashboardType }) => {
                return {
                    url: `${getApiRoute(dashboardType)}insight_trend_line/${
                        dashboardType === "ai_insights" ? `${type}/` : ""
                    }`,
                    method: "POST",
                    body: payload,
                };
            },
            providesTags: (_, __, arg) => [{ type: "TrendData", id: arg.type }],
        }),
        getInsightDetails: builder.query<
            DetailedInsightDataByType,
            InsightsPayload
        >({
            query: ({ type, payload }) => ({
                url: `/customer_intelligence/insight_detail_view/${type}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (_, __, arg) => [
                { type: "InsightDetails", id: arg.type },
            ],
        }),
        getInsightStats: builder.query<InsightStatistics, InsightsStatsPayload>(
            {
                query: ({ id, payload }) => ({
                    url: `/customer_intelligence/insight_stats/${id}/`,
                    method: "POST",
                    body: payload,
                }),
                providesTags: (_, __, arg) => [
                    { type: "InsightStats", id: arg.id },
                ],
            }
        ),
        getInsightTopics: builder.query<InsightTopics, InsightsStatsPayload>({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/topics/${id}/`,
                method: "POST",
                body: payload,
            }),
            providesTags: (_, __, arg) => [
                { type: "InsightStats", id: arg.id },
            ],
        }),
        downloadInsightReport: builder.mutation<
            DownloadReport,
            InsightsDownloadPayload & { dashboardType: CIdashboardType }
        >({
            query: ({ payload, dashboardType }) => ({
                url: `${getApiRoute(dashboardType)}report_download/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        downloadDbInsightReport: builder.mutation<
            DownloadReport,
            InsightDashboardDetailsPayload & { dashboardType: CIdashboardType }
        >({
            query: ({ payload, dashboardType }) => ({
                url: `${getApiRoute(dashboardType)}db_report_download/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        downloadDbInsightDumpReport: builder.mutation<
            DownloadReport,
            InsightDashboardDetailsPayload & { dashboardType: CIdashboardType }
        >({
            query: ({ payload, dashboardType }) => ({
                url: `${getApiRoute(dashboardType)}db_report_dump/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),

        shareSnippet: builder.mutation<void, ShareSnippet>({
            query: (payload) => ({
                url: "/share/share_meeting/",
                method: "POST",
                body: payload,
            }),
        }),
        downloadInsightDumpReport: builder.mutation<
            DownloadReport,
            InsightsStatsPayload
        >({
            query: ({ id, payload }) => ({
                url: `/customer_intelligence/report/insight/${
                    id && id !== -1 ? id : "sentiment/neutral"
                }/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),

        // AI Insights
        getAiInsightData: builder.query<
            Array<Omit<AiInsightDataByType, "insights">>,
            CIFiltersPayload
        >({
            query: (payload) => ({
                url: "/gpt/ai_insight/",
                method: "POST",
                body: payload,
            }),
            providesTags: ["AiInsights"],
        }),
        getAiInsightDataByQuestionId: builder.query<
            AiInsightDataByType,
            { id: number } & CIFiltersPayload
        >({
            query: ({ id, ...payload }) => ({
                url: `/gpt/ai_insight/${id}/`,
                method: "POST",
                body: payload,
            }),
        }),
        getAIInsightStats: builder.query<
            InsightStatistics,
            InsightsStatsPayload
        >({
            query: ({ id, payload }) => ({
                url: `/gpt/ai_insight_stats/${id}/`,
                method: "POST",
                body: payload,
            }),
        }),
        downloadAiInsightReport: builder.mutation<
            DownloadReport,
            CIFiltersPayload
        >({
            query: (payload) => ({
                url: "gpt/ai_insight/report_download/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        downloadAiInsightDumpReport: builder.mutation<
            DownloadReport,
            InsightsStatsPayload
        >({
            query: ({ id, payload }) => ({
                url: `gpt/ai_insight/meeting_dump/${id}/`,
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                    });
                } catch (err) {}
            },
        }),
        getCIInsightsInternal: builder.query<CIInsightResponse, void>({
            query: () => ({
                url: "/customer_intelligence/ci_insight/",
            }),
            providesTags: [{ type: "InternalCI", id: "LIST" }],
        }),
        runInsightAuditCommand: builder.query<
            void,
            {
                start_time: string;
                end_time: string;
            }
        >({
            query: (params) => ({
                url: "/customer_intelligence/ci_insight_audit/",
                params,
            }),
        }),
        errorCheck: builder.query<void, ErrorCheckPayload>({
            query: ({ id, ...params }) => ({
                url: `/audit/subfilter/check/${id}/`,
                params,
            }),
        }),
        getInsight: builder.query<CIInsight, string>({
            query: (id) => ({
                url: `customer_intelligence/ci_insight/${id}/`,
            }),
        }),
        createInsight: builder.mutation<
            CIInsight,
            {
                name: string;
                sub_insight: Array<NewSubInsightPayload>;
                type: InsightType;
                level: number;
            }
        >({
            query: (payload) => ({
                url: "customer_intelligence/ci_insight/",
                method: "POST",
                body: payload,
            }),
        }),
        saveInsight: builder.mutation<
            CIInsight,
            {
                id: number;
                name: string;
                level: number;
                sub_insight: Array<ISubInsight | NewSubInsightPayload>;
                type: InsightType;
                is_disabled?: boolean;
            }
        >({
            query: (payload) => ({
                url: `customer_intelligence/ci_insight/${payload.id}/`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: () => [{ type: "InternalCI", id: "LIST" }],
        }),
        deleteInsight: builder.mutation<CIInsight, number | string>({
            query: (id) => ({
                url: `customer_intelligence/ci_insight/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: () => [{ type: "InternalCI", id: "LIST" }],
        }),
        createFilter: builder.mutation<
            AIFilter,
            Omit<CreateAIFilterPayload, "id">
        >({
            query: (payload) => ({
                url: "audit/filter/",
                method: "POST",
                body: payload,
            }),
        }),
        saveFilter: builder.mutation<
            AIFilter,
            CreateAIFilterPayload & { dashboard_type: "audit" | "ci" }
        >({
            query: ({ id, dashboard_type, ...payload }) => ({
                url: `audit/${
                    dashboard_type === "audit" ? "filter" : "insight_filter"
                }/${id}/`,
                method: "PATCH",
                body: payload,
            }),
        }),
        getFilter: builder.query<
            AIFilter,
            {
                id: string;
                dashboard_type: "audit" | "ci";
            }
        >({
            query: ({ id, dashboard_type }) => ({
                url: `audit/${
                    dashboard_type === "audit" ? "filter" : "insight_filter"
                }/${id}/`,
                method: "GET",
            }),
        }),
        deleteSubfilter: builder.query<void, string>({
            query: (id) => ({
                url: `audit/sub_filter/destory/${id}/`,
                method: "DELETE",
            }),
        }),
        deletefilter: builder.query<void, string>({
            query: (id) => ({
                url: `audit/insight_filter/destory/${id}/`,
                method: "DELETE",
            }),
        }),
        getCIGPTTypes: builder.query<
            { ci_types: CIGPTType[] },
            { ci_types: boolean }
        >({
            query: (params) => ({
                url: "/analytics/ci_gpt/",
                method: "GET",
                params,
            }),
        }),
        getGptQuestions: builder.query<CIGPTQuestion[], void>({
            query: () => ({
                url: "/gpt/question/list/?is_ci_gpt=true",
                method: "GET",
            }),
        }),
        updateCIGPTType: builder.mutation<void, CIGPTType>({
            query: (payload) => ({
                url: "/analytics/ci_gpt/update/",
                method: "POST",
                body: payload,
            }),

            async onQueryStarted(payload, { queryFulfilled, dispatch }) {
                // TO update the list of gpt types if the gpt of type  is changed from the switch
                const patchResult = dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getCIGPTTypes",
                        { ci_types: true },
                        (draft) => {
                            const ciGptType = draft.ci_types.find(
                                (item) => item.type === payload.type
                            );
                            if (ciGptType) {
                                ciGptType.status = payload.status;
                            }
                        }
                    )
                );
                // TO update the list of gpt questions if the gpt of type  is changed from the switch
                const patchListResult = dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getGptQuestions",
                        undefined,
                        (draft) => {
                            draft.map((item) => {
                                if (
                                    item.metadata.insight_type === payload.type
                                ) {
                                    item.metadata.is_ci_gpt = payload.status;
                                }
                                return item;
                            });
                        }
                    )
                );
                queryFulfilled.catch(() => {
                    patchResult.undo();
                    patchListResult.undo();
                });
            },
        }),
        updateGptQuestion: builder.mutation<
            CIGPTQuestion,
            Pick<
                CIGPTQuestion,
                "id" | "question_text" | "description" | "metadata"
            >
        >({
            query: (payload) => ({
                url: `/gpt/questions/${payload.id}/`,
                method: "PATCH",
                body: payload,
            }),

            async onQueryStarted(payload, { queryFulfilled, dispatch }) {
                const patchResult = dispatch(
                    customerIntelligenceApiSlice.util.updateQueryData(
                        "getGptQuestions",
                        undefined,
                        (draft) => {
                            return draft.map((item) =>
                                item.id === payload.id
                                    ? { ...item, ...payload }
                                    : item
                            );
                        }
                    )
                );
                queryFulfilled.catch(() => {
                    patchResult.undo();
                });
            },
        }),
    }),
});

export const {
    useGetCITabsQuery,
    useGetCITabQuery,
    useGetCITabGraphQuery,
    useUpdateCITabSequenceMutation,
    useCreateCITabMutation,
    useUpdateCITabMutation,
    useDeleteCITabMutation,
    useGetCIPhraseSnippetsQuery,
    useLazyGetCIPhraseSnippetsQuery,
    useAddCITabPhraseMutation,
    useUpdateCITabPhraseMutation,
    useRemoveCITabPhraseMutation,
    useSendWordCloudMutation,
    useSendReportMutation,
    useSendTabReportMutation,
    useGetPhraseStatisticsQuery,
    useGetInsightSummaryQuery,
    useGetInsightByTypeQuery,
    useGetInsightDashboardDetailsQuery,
    useLazyGetInsightDashboardDetailsQuery,
    useGetAllInsightsByTypeQuery,
    useLazyGetAllInsightsByTypeQuery,
    useGetInsightDetailsQuery,
    useGetInsightTrendDataQuery,
    useGetInsightStatsQuery,
    useGetInsightTopicsQuery,
    useDownloadInsightReportMutation,
    useDownloadDbInsightDumpReportMutation,
    useDownloadDbInsightReportMutation,
    useShareSnippetMutation,
    useDownloadInsightDumpReportMutation,
    useGetAiInsightDataQuery,
    useGetAiInsightDataByQuestionIdQuery,
    useGetAIInsightStatsQuery,
    useDownloadAiInsightReportMutation,
    useDownloadAiInsightDumpReportMutation,
    useLazyGetAiInsightDataQuery,
    useGetCIInsightsInternalQuery,
    useLazyRunInsightAuditCommandQuery,
    useLazyErrorCheckQuery,
    useGetInsightQuery,
    useSaveInsightMutation,
    useCreateInsightMutation,
    useDeleteInsightMutation,
    useCreateFilterMutation,
    useSaveFilterMutation,
    useGetFilterQuery,
    useLazyDeleteSubfilterQuery,
    useLazyDeletefilterQuery,
    useGetCIGPTTypesQuery,
    useUpdateCIGPTTypeMutation,
    useGetGptQuestionsQuery,
    useUpdateGptQuestionMutation,
    useGetCIInsightByTypeQuery,
    useLazyGetFilterQuery,
} = customerIntelligenceApiSlice;
